/**
 * PV module library service (SD-4.4 reads, SD-8.4 writes; FR-26/FR-27).
 *
 * Reads `pv_modules` — RLS already scopes rows to global-plus-own-tenant, so no tenant filter
 * is repeated here; a client-side filter would be a second copy of the policy that drifts.
 *
 * THREE READS, DELIBERATELY DIFFERENT:
 *   - `listActiveModules()` — the catalogue a picker shows: active only.
 *   - `listModulesForManagement()` — the SD-8.4 dialog: INCLUDES inactive rows, because a
 *     deactivated module that vanishes from the management list is indistinguishable from a
 *     deleted one, and deletion is exactly what this table refuses to offer.
 *   - `getModuleById()` — resolution for an EXISTING reference: includes INACTIVE rows, because
 *     FR-20 says a module referenced by a frozen version stays resolvable. A picker must not
 *     offer a deactivated module; a design that already uses one must not lose its geometry.
 *
 * `resolveDefaultPanelModule()` is the FR-27 preselection: the tenant's chosen default
 * (solar_design_settings.default_module_id → pv_modules row) as a PanelModule, or null when no
 * default is chosen or the row cannot be read — the caller falls back to DEFAULT_MODULE, so a
 * broken read degrades to the shipped geometry rather than a blank canvas.
 *
 * WRITES (SD-8.4) CANNOT PRODUCE A GLOBAL ROW BY ANY REQUEST SHAPE, at two layers on purpose:
 * the payload here is built from NAMED fields with `scope: 'tenant'` as a literal — arbitrary
 * keys on the input cannot reach the wire — and `owner_tenant_id` comes from the SAME
 * `get_current_user_tenant_id()` the 20260805g INSERT policy compares against (an RPC to it,
 * so a superadmin operating in a switched tenant writes to the tenant they are acting in).
 * The database is still the enforcer — 20260805g has NO write policy matching global rows —
 * and `supabase/tests/20260805g_pv_modules_test.sql` asserts that directly against the API
 * surface. Updates never carry scope or owner, so a tenant row cannot be re-scoped; deactivation
 * is an UPDATE of is_active — there is deliberately NO delete function in this file (FR-20).
 *
 * Write failures THROW the PostgREST error verbatim so the dialog can route it through
 * saveErrorMessage and keep "you may not" distinguishable from "try again" (the SD-8.3 rule).
 */

import { supabase } from '@/lib/supabaseClient';
import { type PanelModule, panelModuleFromRow } from '@/lib/solar/panelGeometry';
import { getSolarDesignSettings } from '@/services/solarDesignSettingsService';

export interface PvModuleRow {
  id: string;
  scope: 'global' | 'tenant';
  owner_tenant_id: string | null;
  manufacturer: string;
  model: string;
  watts: number;
  width_mm: number;
  height_mm: number;
  is_active: boolean;
}

const COLUMNS = 'id, scope, owner_tenant_id, manufacturer, model, watts, width_mm, height_mm, is_active';

export async function listActiveModules(): Promise<PvModuleRow[]> {
  const { data, error } = await supabase
    .from('pv_modules')
    .select(COLUMNS)
    .eq('is_active', true)
    .order('manufacturer')
    .order('model');
  if (error) {
    console.error('[pvModulesService] Failed to list modules:', error);
    return [];
  }
  return (data ?? []) as PvModuleRow[];
}

/**
 * The management dialog's list: includes INACTIVE rows on purpose — see the header note.
 * Ordered tenant-first so "Yours" leads, then by manufacturer and model like the picker.
 */
export async function listModulesForManagement(): Promise<PvModuleRow[]> {
  const { data, error } = await supabase
    .from('pv_modules')
    .select(COLUMNS)
    .order('scope', { ascending: false })
    .order('manufacturer')
    .order('model');
  if (error) {
    console.error('[pvModulesService] Failed to list modules for management:', error);
    return [];
  }
  return (data ?? []) as PvModuleRow[];
}

/** Includes inactive rows on purpose — see the FR-20 note above. */
export async function getModuleById(id: string): Promise<PvModuleRow | null> {
  const { data, error } = await supabase
    .from('pv_modules')
    .select(COLUMNS)
    .eq('id', id)
    .maybeSingle();
  if (error) {
    console.error('[pvModulesService] Failed to resolve module:', error);
    return null;
  }
  return (data as PvModuleRow) ?? null;
}

/** The tenant default as drawable geometry, or null → caller uses DEFAULT_MODULE. */
export async function resolveDefaultPanelModule(): Promise<PanelModule | null> {
  const settings = await getSolarDesignSettings();
  if (!settings || settings.missing || !settings.row.default_module_id) return null;
  const row = await getModuleById(settings.row.default_module_id);
  return row ? panelModuleFromRow(row) : null;
}

export interface TenantModuleInput {
  manufacturer: string;
  model: string;
  watts: number;
  width_mm: number;
  height_mm: number;
}

/** NAMED fields only: whatever else rides on `input` never reaches the wire. */
const moduleFields = (input: TenantModuleInput) => ({
  manufacturer: input.manufacturer.trim(),
  model: input.model.trim(),
  watts: input.watts,
  width_mm: input.width_mm,
  height_mm: input.height_mm,
});

export async function createTenantModule(input: TenantModuleInput): Promise<PvModuleRow> {
  // The same function the INSERT policy's WITH CHECK calls, so the payload and the policy can
  // only agree — including for a superadmin acting inside a switched tenant.
  const { data: tenantId, error: tenantError } = await supabase.rpc('get_current_user_tenant_id');
  if (tenantError || !tenantId) {
    throw tenantError ?? new Error('No tenant context — cannot create a module.');
  }
  const { data, error } = await supabase
    .from('pv_modules')
    .insert({ ...moduleFields(input), scope: 'tenant', owner_tenant_id: tenantId })
    .select(COLUMNS)
    .single();
  if (error) throw error;
  return data as PvModuleRow;
}

/** Never carries scope or owner_tenant_id: a tenant row cannot be re-scoped from here. */
export async function updateTenantModule(
  id: string,
  input: TenantModuleInput
): Promise<PvModuleRow> {
  const { data, error } = await supabase
    .from('pv_modules')
    .update(moduleFields(input))
    .eq('id', id)
    .select(COLUMNS)
    .single();
  if (error) throw error;
  return data as PvModuleRow;
}

/**
 * Deactivate (or reactivate). This is the WHOLE removal story: layouts already built with the
 * module keep using it (FR-20 — getModuleById resolves inactive rows), so there is no delete.
 */
export async function setModuleActive(id: string, isActive: boolean): Promise<PvModuleRow> {
  const { data, error } = await supabase
    .from('pv_modules')
    .update({ is_active: isActive })
    .eq('id', id)
    .select(COLUMNS)
    .single();
  if (error) throw error;
  return data as PvModuleRow;
}
